import React, { Suspense, lazy, useState, useEffect, useRef, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Importar páginas
import Sidebar from "./components/layout/Sidebar";
import Header from "./components/layout/Header";
import PremiumLoginPage from "./components/auth/PremiumLoginPage";
import BrandLockup from "./components/branding/BrandLockup";
import CollaboratorIntro from "./components/branding/CollaboratorIntro";
import { getBackendUrl } from "./utils/api";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const TarefasPage = lazy(() => import("./pages/TarefasPage"));
const DocumentosPage = lazy(() => import("./pages/DocumentosPageV2"));
const ClientesPageNew = lazy(() => import("./pages/ClientesPageNew"));
const CertificadosPage = lazy(() => import("./pages/CertificadosPageNew"));
const ColaboradoresPage = lazy(() => import("./pages/ColaboradoresPage"));
const ObrigacoesAcessoriasPage = lazy(() => import("./pages/ObrigacoesAcessoriasPage"));
const FinanceiroPage = lazy(() => import("./pages/FinanceiroPage"));
const ConfiguracoesPage = lazy(() => import("./pages/ConfiguracoesPage"));
const PortalColaborador = lazy(() => import("./pages/PortalColaborador"));
const MobileUploadPage = lazy(() => import("./pages/MobileUploadPage"));
const MannuAppPage = lazy(() => import("./pages/MannuAppPage"));
const ChatOperacionalPage = lazy(() => import("./pages/ChatOperacionalPage"));
const AgenteLBCCPage = lazy(() => import("./pages/AgenteLBCCPage"));
const ConsultasPage = lazy(() => import("./pages/ConsultasPage"));
const EditorDocumentoPage = lazy(() => import("./pages/EditorDocumentoPage"));
const SiegPage = lazy(() => import("./pages/SiegPage"));
const QuestorPage = lazy(() => import("./pages/QuestorPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const MAIN_PAGES = new Set(['dashboard', 'tarefas', 'documentos', 'clientes', 'colaboradores', 'financeiro', 'certificados', 'obrigacoes', 'consultas', 'editor-documento', 'sieg', 'questor', 'config']);
axios.defaults.withCredentials = true;

const isPlaceholderAuthToken = (value) => !value || value === 'cookie-authenticated';

function PageLoader({ label = 'Carregando...' }) {
  return (
    <div className="min-h-[320px] bg-slate-100 flex items-center justify-center" data-testid="page-loader">
      <div className="text-center">
        <svg className="w-10 h-10 text-emerald-600 animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <p className="text-sm text-slate-600">{label}</p>
      </div>
    </div>
  );
}

function scrollDocumentToTop() {
  if (typeof window === 'undefined') return;
  window.requestAnimationFrame(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  });
}

const getAuthorizationHeader = (headers) => {
  if (!headers) return '';
  if (typeof headers.get === 'function') return headers.get('Authorization') || headers.get('authorization') || '';
  return headers.Authorization || headers.authorization || '';
};

const deleteAuthorizationHeader = (headers) => {
  if (!headers) return;
  if (typeof headers.delete === 'function') {
    headers.delete('Authorization');
    headers.delete('authorization');
    return;
  }
  delete headers.Authorization;
  delete headers.authorization;
};

axios.interceptors.request.use((config) => {
  config.withCredentials = true;
  const authorization = getAuthorizationHeader(config.headers);
  if (/^Bearer\s+cookie-authenticated$/i.test(String(authorization || '').trim())) {
    deleteAuthorizationHeader(config.headers);
  }
  return config;
});

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isTransientAuthError = (error) => {
  const status = error?.response?.status;
  return !error?.response || [502, 503, 504].includes(status);
};

const withAuthRetry = async (requestFn, retries = 1) => {
  try {
    return await requestFn();
  } catch (error) {
    if (retries > 0 && isTransientAuthError(error)) {
      await delay(700);
      return withAuthRetry(requestFn, retries - 1);
    }
    throw error;
  }
};

const formatApiErrorDetail = (error) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;

  if (status === 502 || status === 503) {
    return 'Servidor indisponível no momento. Tente novamente em alguns segundos.';
  }

  if (!error?.response) {
    return 'Não foi possível conectar ao servidor.';
  }

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item?.msg === 'string' ? item.msg : null))
      .filter(Boolean)
      .join(' ') || 'Erro ao processar sua solicitação';
  }

  if (typeof error?.response?.data === 'string' && error.response.data.trim()) {
    return error.response.data.replace(/<[^>]+>/g, ' ').trim() || 'Erro ao processar sua solicitação';
  }

  return 'Erro ao processar sua solicitação';
};

const getInitialMainPage = (isAdmin) => {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const page = params.get('page');
    if (page === 'painel-whatsapp') {
      params.set('page', 'config');
      params.set('tab', 'whatsapp');
      window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
      return 'config';
    }
    if (page === 'midia' || page === 'controladoria') {
      const fallback = isAdmin ? 'dashboard' : 'tarefas';
      params.set('page', fallback);
      window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
      return fallback;
    }
    if (page && MAIN_PAGES.has(page)) {
      return page;
    }
  }

  return isAdmin ? 'dashboard' : 'tarefas';
};

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [portalIntroPending, setPortalIntroPending] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await withAuthRetry(() => axios.get(`${API}/auth/me`, { withCredentials: true }));
      setUser(response.data);
      try {
        const tokenResponse = await withAuthRetry(() => axios.get(`${API}/auth/session-token`, { withCredentials: true }));
        setToken(tokenResponse.data?.token || 'cookie-authenticated');
      } catch {
        setToken('cookie-authenticated');
      }
    } catch (error) {
      if ([401, 403].includes(error?.response?.status)) {
        setToken(null);
        delete axios.defaults.headers.common["Authorization"];
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await withAuthRetry(() => axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true }));
    setPortalIntroPending(response.data.user?.role === 'colaborador');
    window.sessionStorage.removeItem('dashboard:hero-group');
    setToken(isPlaceholderAuthToken(response.data.token) ? 'cookie-authenticated' : response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name, cnpj) => {
    const response = await withAuthRetry(() => axios.post(`${API}/auth/register`, { email, password, name, cnpj }, { withCredentials: true }));
    window.sessionStorage.removeItem('dashboard:hero-group');
    setToken(isPlaceholderAuthToken(response.data.token) ? 'cookie-authenticated' : response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const loginWithGoogle = async (sessionId) => {
    const response = await withAuthRetry(() => axios.post(`${API}/auth/google/session`, { session_id: sessionId }, { withCredentials: true }));
    setPortalIntroPending(response.data.user?.role === 'colaborador');
    window.sessionStorage.removeItem('dashboard:hero-group');
    setToken(isPlaceholderAuthToken(response.data.token) ? 'cookie-authenticated' : response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    setPortalIntroPending(false);
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Logout local deve prosseguir mesmo se a chamada de limpeza falhar.
    }
    setToken(null);
    setUser(null);
    window.sessionStorage.removeItem('dashboard:hero-group');
    delete axios.defaults.headers.common["Authorization"];
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, loginWithGoogle, logout, loading, setUser, portalIntroPending, setPortalIntroPending }}>
      {children}
    </AuthContext.Provider>
  );
};

// Página de Login
function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(formData.email, formData.password);
      const pendingRedirect = window.sessionStorage.getItem('postLoginRedirect');
      if (pendingRedirect) {
        window.sessionStorage.removeItem('postLoginRedirect');
        navigate(pendingRedirect, { replace: true });
      }
      toast.success('Login realizado com sucesso!');
    } catch (err) {
      const message = formatApiErrorDetail(err);
      setError(message);
      toast.error(`Erro: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PremiumLoginPage
      formData={formData}
      setFormData={setFormData}
      handleSubmit={handleSubmit}
      loading={loading}
      error={error}
    />
  );
}

// Auth Callback Component - Handles Google OAuth redirect
function AuthCallback() {
  const { loginWithGoogle, setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          setError('Session ID não encontrado');
          return;
        }

        // Exchange session_id for JWT token
        await loginWithGoogle(sessionId);
        
        // Clear hash and redirect
        window.history.replaceState(null, '', window.location.pathname);
        toast.success('Login com Google realizado com sucesso!');
        
      } catch (err) {
        console.error('Google auth error:', err);
        setError(err.response?.data?.detail || 'Erro ao fazer login com Google');
        toast.error('Erro ao fazer login com Google');
        // Redirect back to login after delay
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      }
    };

    processAuth();
  }, [loginWithGoogle, setUser]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <p className="text-slate-600 mb-2">Erro na autenticação</p>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="text-center">
        <svg className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <p className="text-slate-600">Autenticando com Google...</p>
      </div>
    </div>
  );
}

// Layout Principal
function MainLayout() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const mainRef = useRef(null);
  
  // Colaboradores sempre iniciam em Tarefas
  const [currentPage, setCurrentPageState] = useState(() => getInitialMainPage(isAdmin));
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const setCurrentPage = (requestedPage, query = {}) => {
    let page = requestedPage;
    if (typeof page === 'string' && page.startsWith('clientes/')) {
      window.sessionStorage.setItem('clientes:auto-open-id', page.slice('clientes/'.length));
      page = 'clientes';
    }
    if (!MAIN_PAGES.has(page)) return;
    if (!isAdmin && page !== 'tarefas') page = 'tarefas';

    const params = new URLSearchParams({ page });
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
    });
    const nextUrl = `${window.location.pathname}?${params.toString()}`;
    if (`${window.location.pathname}${window.location.search}` !== nextUrl) {
      navigate(nextUrl);
    }
    setCurrentPageState(page);
  };

  useEffect(() => {
    const restorePageFromHistory = () => {
      const page = getInitialMainPage(isAdmin);
      setCurrentPageState(!isAdmin && page !== 'tarefas' ? 'tarefas' : page);
    };
    window.addEventListener('popstate', restorePageFromHistory);
    return () => window.removeEventListener('popstate', restorePageFromHistory);
  }, [isAdmin]);

  // Redirecionar colaborador se tentar acessar página restrita
  useEffect(() => {
    if (!isAdmin && currentPage !== 'tarefas') {
      setCurrentPageState('tarefas');
    }
  }, [isAdmin, currentPage]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', currentPage);

    if (currentPage !== 'tarefas') {
      params.delete('status');
      params.delete('vencidas');
      params.delete('id');
    }

    if (currentPage !== 'documentos') {
      params.delete('documento');
    }

    if (currentPage !== 'documentos' && currentPage !== 'clientes') {
      params.delete('cliente');
    }

    const nextQuery = params.toString();
    const nextUrl = nextQuery ? `${window.location.pathname}?${nextQuery}` : window.location.pathname;
    window.history.replaceState(null, '', nextUrl);
  }, [currentPage]);

  useEffect(() => {
    setMobileSidebarOpen(false);
    scrollDocumentToTop();
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  const pageConfig = {
    dashboard: { title: 'Dashboard', subtitle: 'Visão geral do sistema' },
    tarefas: { title: 'Tarefas', subtitle: 'Gerenciar tarefas e obrigações' },
    documentos: { title: 'Documentos', subtitle: 'Centro de documentos' },
    clientes: { title: 'Clientes', subtitle: 'Gerenciar clientes' },
    colaboradores: { title: 'Equipe', subtitle: 'Gerenciar colaboradores e permissões' },
    financeiro: { title: 'Financeiro', subtitle: '' },
    config: { title: 'Config', subtitle: '' },
    certificados: { title: 'Certificados', subtitle: 'Gerenciar certificados digitais' },
    obrigacoes: { title: 'Obrigações Acessórias', subtitle: 'Controle de obrigações fiscais' },
    consultas: { title: 'Consultas', subtitle: 'Crédito, localização e veículos (CPF/CNPJ)' },
    'editor-documento': { title: 'Editor LBCC', subtitle: 'Documentos timbrados para imprimir ou salvar em PDF' },
    sieg: { title: 'SIEG', subtitle: 'Captura e conferência de documentos fiscais' },
    questor: { title: 'Operação LBCC', subtitle: 'Fechamento por empresa e competência' },
  };

  const renderPage = () => {
    const props = { token, selectedCliente, setSelectedCliente, setCurrentPage, user };

    switch (currentPage) {
      case 'dashboard':
        return <DashboardPage {...props} />;
      case 'tarefas':
        return <TarefasPage {...props} />;
      case 'documentos':
        return <DocumentosPage {...props} />;
      case 'clientes':
        return <ClientesPageNew {...props} />;
      case 'colaboradores':
        return <ColaboradoresPage {...props} />;
      case 'financeiro':
        return <FinanceiroPage {...props} />;
      case 'config':
        return <ConfiguracoesPage {...props} />;
      case 'certificados':
        return <CertificadosPage {...props} />;
      case 'obrigacoes':
        return <ObrigacoesAcessoriasPage {...props} />;
      case 'consultas':
        return <ConsultasPage {...props} />;
      case 'editor-documento':
        return <EditorDocumentoPage {...props} />;
      case 'sieg':
        return <SiegPage {...props} />;
      case 'questor':
        return <QuestorPage {...props} />;
      default:
        return <DashboardPage {...props} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Sidebar 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage} 
        user={user}
        onLogout={logout}
        mobileOpen={mobileSidebarOpen}
        onCloseMobile={() => setMobileSidebarOpen(false)}
      />
      
      <div className="lg:ml-64">
        <Header 
          title={pageConfig[currentPage]?.title || 'Dashboard'} 
          subtitle={pageConfig[currentPage]?.subtitle}
          token={token}
          setCurrentPage={setCurrentPage}
          onOpenMobileSidebar={() => setMobileSidebarOpen(true)}
        />
        
        <main ref={mainRef} className={currentPage === 'documentos' ? 'documents-page-main' : 'overflow-x-hidden p-4 pb-8 sm:p-6'}>
          <Suspense fallback={<PageLoader label="Carregando página..." />}>
            {renderPage()}
          </Suspense>
        </main>
      </div>
    </div>
  );
}

// Componente de Login para Portal
function PortalLoginPage() {
  const { login, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await login(email, password);
      
      if (response.user?.role !== 'colaborador') {
        await logout();
        setError('Este portal é exclusivo para colaboradores. Use o login principal.');
        setLoading(false);
        return;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lbcc-navy-gradient min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md" data-testid="portal-login-page">
        <div className="text-center mb-8">
          <div className="flex justify-center">
            <BrandLockup
              surface="light"
              centered
              title="Portal do Colaborador"
              subtitle="LBCCorex HUB"
              logoClassName="h-20 w-20"
              dataTestId="portal-login-brand-lockup"
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="portal-login-error-alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="portal-login-form">
          <div data-testid="portal-login-email-field">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-testid="portal-login-email-label">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="seu@email.com"
              required
              data-testid="portal-login-email-input"
            />
          </div>

          <div data-testid="portal-login-password-field">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-testid="portal-login-password-label">Senha</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="••••••••"
              required
              data-testid="portal-login-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
            data-testid="portal-login-submit-button"
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        <div className="mt-6 text-center" data-testid="portal-login-back-section">
          <a href="/" className="text-sm text-slate-500 hover:text-emerald-600" data-testid="portal-login-back-link">
            ← Voltar para o site principal
          </a>
        </div>
      </div>
    </div>
  );
}

// Página do Portal (com verificação de autenticação)
function PortalPage() {
  const { user, token, logout, loading, portalIntroPending, setPortalIntroPending } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Se não logado, mostrar login do portal
  if (!user) {
    return <PortalLoginPage />;
  }

  // Se é admin, redirecionar para o dashboard principal
  if (user.role === 'admin') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-[#0D2B4A] mb-2">Acesso Restrito</h2>
          <p className="text-slate-600 mb-6">Este portal é exclusivo para colaboradores. Como administrador, use o painel principal.</p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
          >
            Ir para o Painel Principal
          </a>
        </div>
      </div>
    );
  }

  // Colaborador logado - mostrar portal
  return (
    <>
      <div id="collaborator-portal-content" tabIndex={-1} className="outline-none" inert={portalIntroPending || undefined} aria-hidden={portalIntroPending || undefined}>
        <Suspense fallback={<PageLoader label="Abrindo portal..." />}>
          <PortalColaborador user={user} token={token} onLogout={logout} />
        </Suspense>
      </div>
      {portalIntroPending && user.role === 'colaborador' && <CollaboratorIntro onComplete={() => {
        setPortalIntroPending(false);
        window.requestAnimationFrame(() => document.getElementById('collaborator-portal-content')?.focus({ preventScroll: true }));
      }} />}
    </>
  );
}

function MannuAppRoute() {
  const { user, token, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center" data-testid="mannu-app-loading-state">
        <div className="text-center">
          <svg className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-slate-600">Abrindo seu app IA...</p>
        </div>
      </div>
    );
  }

  if (!user || !token) {
    window.sessionStorage.setItem('postLoginRedirect', '/app-ia');
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<PageLoader label="Abrindo seu app IA..." />}>
      <MannuAppPage token={token} user={user} />
    </Suspense>
  );
}

function ChatOperacionalRoute() {
  const { user, token, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center" data-testid="chat-operacional-loading-state">
        <div className="text-center">
          <svg className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-slate-600">Abrindo o chat operacional...</p>
        </div>
      </div>
    );
  }

  if (!user || !token) {
    window.sessionStorage.setItem('postLoginRedirect', '/chat-operacional');
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<PageLoader label="Abrindo o chat operacional..." />}>
      <ChatOperacionalPage token={token} user={user} />
    </Suspense>
  );
}

function AgenteLBCCRoute() {
  const { user, token, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center" data-testid="agente-lbcc-loading-route">
        <div className="text-center">
          <svg className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-slate-600">Abrindo o Agente LBCC...</p>
        </div>
      </div>
    );
  }

  if (!user || !token) {
    window.sessionStorage.setItem('postLoginRedirect', '/agente-lbcc');
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<PageLoader label="Abrindo o Agente LBCC..." />}>
      <AgenteLBCCPage token={token} user={user} />
    </Suspense>
  );
}

function AppContent() {
  const { user, token, logout, loading } = useAuth();

  // CRITICAL: Check URL fragment (not query params) for session_id
  // This must happen synchronously during render, NOT in useEffect
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <svg className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-slate-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // Colaborador é redirecionado para o portal
  if (user && user.role === 'colaborador') {
    return <Navigate to="/portal" replace />;
  }

  return user ? <MainLayout /> : <LoginPage />;
}

function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/app-ia" element={<MannuAppRoute />} />
        <Route path="/chat-operacional" element={<ChatOperacionalRoute />} />
        <Route path="/agente-lbcc" element={<AgenteLBCCRoute />} />
        <Route path="/privacidade" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />
        <Route path="/privacy-policy" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />
        <Route path="/mobile-upload/:token" element={<Suspense fallback={<PageLoader />}><MobileUploadPage /></Suspense>} />
        <Route path="/portal" element={<PortalPage />} />
        <Route path="/portal/*" element={<PortalPage />} />
        <Route path="/*" element={<AppContent />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <AppRouter />
    </AuthProvider>
  );
}

export default App;
